import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  Dialog,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import clsx from "clsx";
import millify from "millify";
import { useCallback, useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useWallet from "../../hook/useWallet";
import { toUSDFormat } from "../../utils/utils";
import useStyles from "./BuyCreditStyles";

import helperImg from "../../assets/imgs/slot/helperbot.png";

import { toast } from "react-toastify";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import {
  approveHelperCreditAction,
  buyHelperCreditAction,
  getHelperCreditBalance,
  getHelperCreditPrice,
  getHelperCreditSwapTokenBalance,
  getHelperCreditSwapTokenInfo,
} from "../../reducers/profile.slice";
import { SwapToken } from "../../types/Contract/SwapToken";
import TokenImage from "../TokenImage";

interface BuyCreditPopupProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const BuyCreditPopup: React.FC<BuyCreditPopupProps> = ({
  openModal,
  setOpenModal,
}) => {
  const { classes } = useStyles();
  const { account } = useWallet();
  const dispatch = useAppDispatch();

  const [sellUSDAmount, setSellUSDAmount] = useState("");
  const [sellTokenAmount, setSellTokenAmount] = useState(0);
  const [activeMaxBtn, setActiveMaxBtn] = useState(false);
  const [selectedToken, setSelectedToken] = useState<SwapToken | null>(null);

  const {
    helperCreditSwapTokenInfo: swapTokenInfo,
    helperCreditSwapTokenBalances: swapTokenBalances,
    helperCreditPrice: price,
  } = useAppSelector((state) => state.profile);

  const { signMsg, signature } = useAppSelector((state) => state.auth);

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleFromSwapTokenChange = (e: SelectChangeEvent<number>) => {
    setSelectedToken(swapTokenInfo[Number(e.target.value)]);
    setActiveMaxBtn(false);
  };

  const getSwapTokenUSDValue = useCallback(
    (tokenId: number) => {
      const tokenInfo = swapTokenInfo[tokenId];
      return tokenInfo.price * swapTokenBalances[tokenInfo.name];
    },
    [swapTokenInfo, swapTokenBalances]
  );

  const handleClickMax = () => {
    if (!selectedToken) return;

    setSellUSDAmount(
      (swapTokenBalances[selectedToken.name] * selectedToken.price).toString()
    );
    setActiveMaxBtn(true);
  };

  const handleBuyCredit = () => {
    if (!selectedToken) {
      toast.info("Select the token");
      return;
    }

    if (swapTokenBalances[selectedToken.name] < sellTokenAmount) {
      return toast.info("Insufficient balance.");
    }

    if (!swapTokenBalances) {
      return toast.info("Input the USD amount");
    }
    console.log({ sellTokenAmount });

    const buyAction = () => {
      dispatch(
        buyHelperCreditAction({
          account,
          tokenInfo: selectedToken,
          amount: sellTokenAmount,
        })
      )
        .unwrap()
        .then(() => {
          toast.success("Successfully bought the helper credits.");
          dispatch(getHelperCreditSwapTokenInfo());
          dispatch(getHelperCreditPrice());
          dispatch(getHelperCreditBalance({ account, signMsg, signature }));
          setOpenModal(false);
        });
    };

    if (selectedToken.tokenId !== 0) {
      dispatchTxAction(
        dispatch,
        approveHelperCreditAction({
          account,
          amount: sellTokenAmount,
          tokenInfo: selectedToken,
        }),
        () => {
          buyAction();
        }
      );
    } else {
      buyAction();
    }
  };

  useEffect(() => {
    if (!Number(sellUSDAmount) || !selectedToken) {
      setSellTokenAmount(0);
      return;
    }

    setSellTokenAmount(Number(sellUSDAmount) / selectedToken.price);
  }, [sellUSDAmount, selectedToken]);

  useEffect(() => {
    dispatch(getHelperCreditSwapTokenInfo());
    dispatch(getHelperCreditPrice());
  }, [dispatch]);

  useEffect(() => {
    if (swapTokenInfo.length === 0) return;

    dispatch(getHelperCreditSwapTokenBalance({ account, swapTokenInfo }));
  }, [swapTokenInfo, dispatch, account]);

  useEffect(() => {
    if (swapTokenInfo.length && !selectedToken) {
      setSelectedToken(swapTokenInfo[0]);
    }
  }, [swapTokenInfo, selectedToken]);

  return (
    <Dialog open={openModal} onClose={handleClose} className={classes.body}>
      <Box className={classes.modalContent}>
        <Box className={classes.closeIconBody}>
          <Button onClick={handleClose}>
            <CloseIcon htmlColor="gray" />
          </Button>
        </Box>

        <Box className={classes.contentBody}>
          <Box component={"img"} src={helperImg} className={classes.logoImg} />
          <Box className={classes.titleText}>Buy credits</Box>

          <Box className={classes.swapItem}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              $
              <TextField
                value={sellUSDAmount}
                className={classes.sellInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSellUSDAmount(e.target.value);
                  setActiveMaxBtn(false);
                }}
                placeholder="0.0"
              />
            </Box>
            <FormControl className={classes.swapTokenSelect}>
              <Select
                onChange={handleFromSwapTokenChange}
                className={classes.selectItem}
                value={selectedToken?.tokenId}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#1e1f21",
                    },
                  },
                }}
                SelectDisplayProps={{}}
                IconComponent={(props) => (
                  <KeyboardArrowDownIcon
                    {...props}
                    sx={{
                      color: "white !important",
                      marginRight: "24px",
                      fontSize: "32px",
                    }}
                  />
                )}
                sx={{
                  "& .MuiSelect-select": {
                    padding: "8px",
                  },
                }}
              >
                {swapTokenInfo
                  .slice()
                  .filter((token) => token.isEnabled && token.tokenId !== 1)
                  .sort(
                    (a, b) =>
                      getSwapTokenUSDValue(b.tokenId) -
                      getSwapTokenUSDValue(a.tokenId)
                  )
                  .map((item, index) => (
                    <MenuItem
                      value={item.tokenId}
                      key={index}
                      sx={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <TokenImage token={item.name} sx={{ width: "30px" }} />
                        {item.name}
                      </Box>
                      <Box>
                        {millify(Number(swapTokenBalances[item.name]) || 0, {
                          precision: 1,
                        })}{" "}
                        ($
                        {toUSDFormat(
                          Number(swapTokenBalances[item.name] || 0) *
                            swapTokenInfo[item.tokenId].price
                        )}
                        )
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
          <Box className={classes.swapItem}>
            <Box>
              {toUSDFormat(sellTokenAmount, 3)} {selectedToken?.name}
            </Box>
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {selectedToken && account
                ? millify(swapTokenBalances[selectedToken.name] || 0, {
                    precision: 3,
                  })
                : 0}{" "}
              {(selectedToken && selectedToken.name) || ""}
              ($
              {selectedToken
                ? toUSDFormat(
                    swapTokenBalances[selectedToken.name] * selectedToken.price
                  )
                : 2}
              ){" "}
              <Button
                className={clsx(
                  classes.maxButton,
                  activeMaxBtn ? classes.activeButton : null
                )}
                variant="contained"
                onClick={handleClickMax}
              >
                MAX
              </Button>
            </Box>
          </Box>
          <Box className={classes.swapItem}>
            <Box sx={{ alignItems: "center", display: "flex" }}>
              {toUSDFormat(Number(sellUSDAmount) / price, 1)}{" "}
              <Box
                component={"img"}
                src={helperImg}
                sx={{
                  width: 24,
                  height: 24,
                  marginRight: "8px",
                  marginLeft: "8px",
                }}
              ></Box>
            </Box>
          </Box>

          <Button
            variant="contained"
            className={classes.confirmBut}
            onClick={handleBuyCredit}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BuyCreditPopup;
